import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowSquareOut, DownloadSimple, Trash, CircleNotch, ClosedCaptioning, TextAlignLeft, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { apiErrorMessage } from "@/lib/api";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { mediaSrc, fmtDateTime, CAPTURE_LABELS } from "./serviceApi";
import { TranscriptPanel } from "./TranscriptPanel";
import { useMediaPoll } from "./TranscribeUploadModal";

const chip = "inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors";

export function VideoViewer({ client, media, open, onOpenChange, recordId, onMediaChanged, onRecordChanged, onDelete, canDelete, readOnly }) {
  const videoRef = useRef(null);
  const [cc, setCc] = useState(true);
  const [showTx, setShowTx] = useState(false);

  useMediaPoll(client, media, onMediaChanged, (m) => m.captions_status === "processing" || m.captioned_status === "processing" || m.processing_status === "processing");
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const apply = () => { for (const t of v.textTracks) t.mode = cc ? "showing" : "hidden"; };
    apply();
    v.textTracks.addEventListener?.("addtrack", apply);
    return () => v.textTracks.removeEventListener?.("addtrack", apply);
  }, [cc, media?.captions_url, open]);

  if (!media) return null;
  const src = mediaSrc(media.display_url);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onMediaChanged(await client.getMedia(media.id)); } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); } };
  const capBusy = media.captions_status === "processing";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-4xl max-h-[94vh] overflow-y-auto" data-testid="video-viewer">
        <DialogHeader><DialogTitle className="truncate pr-8" data-testid="video-viewer-name">{media.display_name || media.filename}</DialogTitle></DialogHeader>
        {media.processing_status === "processing" && (
          <div className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="video-processing"><CircleNotch size={13} className="animate-spin" /> Preparing the stamped, browser-friendly copy — the original plays meanwhile.</div>
        )}
        {media.stamp_outdated && !readOnly && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="video-stamp-outdated">
            <Warning size={16} /> Service details changed since this video was stamped.
            <button type="button" onClick={() => act(() => api.post(`/service/media/${media.id}/restamp`), "Re-stamping video…")} className="ml-auto font-semibold underline" data-testid="video-restamp-btn">Re-stamp</button>
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-3">
          <video key={src} ref={videoRef} src={src} controls playsInline preload="metadata" className={`service-video bg-black rounded-lg max-h-[56vh] ${showTx ? "lg:w-2/3" : "w-full"}`} data-testid="video-player">
            {media.captions_url && <track kind="captions" srcLang="en" label="English" src={mediaSrc(media.captions_url)} default />}
          </video>
          {showTx && (
            <div className="lg:w-1/3 max-h-[56vh] overflow-y-auto rounded-lg border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap" data-testid="video-transcript-text">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Transcript</div>
              {media.transcript || "No transcript yet."}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {media.captions_url && (
            <button type="button" onClick={() => setCc((v) => !v)} className={`${chip} ${cc ? "border-[#327de3] text-[#1f64c2]" : ""}`} aria-pressed={cc} data-testid="video-cc-toggle">
              <ClosedCaptioning size={14} /> CC {cc ? "on" : "off"}
            </button>
          )}
          {media.transcript && (
            <button type="button" onClick={() => setShowTx((v) => !v)} className={chip} data-testid="video-transcript-toggle"><TextAlignLeft size={13} /> {showTx ? "Hide" : "View"} transcript</button>
          )}
          {!media.captions_url && media.captions_status !== "no_speech" && !readOnly && (
            <button type="button" disabled={capBusy} onClick={() => act(() => client.captions(media.id), "Generating captions…")} className={chip} data-testid="video-generate-captions-btn">
              {capBusy ? <CircleNotch size={13} className="animate-spin" /> : <ClosedCaptioning size={13} />} {capBusy ? "Generating captions…" : "Transcribe / Generate Captions"}
            </button>
          )}
          {media.captions_status === "no_speech" && <span className="text-[11px] text-slate-500">No speech detected for captions.</span>}
          <a href={src} target="_blank" rel="noreferrer noopener" className={chip} data-testid="video-open-tab-btn"><ArrowSquareOut size={13} /> Open in new tab</a>
          <a href={mediaSrc(media.download_url)} className={chip} data-testid="video-download-btn"><DownloadSimple size={13} /> Download{media.stamp_status === "stamped" ? " stamped" : ""}</a>
          {media.captions_url && !client.portal && (media.captioned_url ? (
            <a href={mediaSrc(media.captioned_url)} className={chip} data-testid="video-captioned-download-btn"><DownloadSimple size={13} /> Download captioned copy</a>
          ) : (
            <button type="button" disabled={media.captioned_status === "processing"} onClick={() => act(() => client.captionedExport(media.id), "Rendering captioned copy…")} className={chip} data-testid="video-captioned-export-btn">
              {media.captioned_status === "processing" ? <CircleNotch size={13} className="animate-spin" /> : <DownloadSimple size={13} />} {media.captioned_status === "processing" ? "Rendering captioned copy…" : "Download captioned copy"}
            </button>
          ))}
          <span className="text-[11px] text-slate-500">{media.capture_display || fmtDateTime(media.capture_at)} · {CAPTURE_LABELS[media.capture_source] || media.capture_source}</span>
          {canDelete && !readOnly && (
            <Button size="sm" variant="outline" onClick={() => onDelete(media)} className="ml-auto text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="video-delete-btn"><Trash size={13} className="mr-1" /> Delete</Button>
          )}
        </div>
        {media.captions_url && <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><ArrowsClockwise size={11} /> Captions show in the player; the downloadable captioned copy has them burned in.</div>}
        <TranscriptPanel client={client} media={media} recordId={recordId} onMediaChanged={onMediaChanged} onRecordChanged={onRecordChanged} readOnly={readOnly} />
      </DialogContent>
    </Dialog>
  );
}
